// ─── Pure domain helpers for the Query Builder ───────────────────────────────
// No React, no side effects — unit-testable. Single source of truth for
// operator labels, value shaping and config assembly.

/** Human labels for each operator the query language supports. */
export const OPERATOR_LABELS = {
  "=": "equals",
  ">": "greater than",
  "<": "less than",
  ">=": "greater or equal",
  "<=": "less or equal",
  between: "between",
  in: "in (any of)",
};

/** Operators that take two values (a range). */
export const isRangeOperator = (operator) => operator === "between";

/** Operators that take a list of values. */
export const isListOperator = (operator) => operator === "in";

/**
 * Creates an empty filter row bound to a starting field/operator.
 * @param {string} field the field key to default to.
 * @param {string} operator the operator to default to.
 * @returns {{field: string, operator: string, value: string, value2: string}}
 */
export const emptyFilter = (field = "", operator = "=") => ({
  field,
  operator,
  value: "",
  value2: "",
});

/**
 * Coerces a raw string input to the type a field expects.
 * @param {string} raw the raw input value.
 * @param {string} type the field's logical type (e.g. "number").
 * @returns {string|number} the coerced value.
 */
const coerce = (raw, type) => {
  if (type === "number") {
    const n = Number(raw);
    return Number.isNaN(n) ? raw : n;
  }
  if (type === "boolean") {
    return raw === "" ? raw : Number(raw);
  }
  return raw;
};

/**
 * Converts a stored/API filter clause back into an editable builder row (the
 * inverse of {@link toApiFilter}) so a saved query can be reloaded for editing.
 * @param {object} apiFilter the persisted clause ({ field, operator, value }).
 * @returns {{field: string, operator: string, value: string, value2: string}} an editable row (no _id).
 */
export const apiFilterToRow = (apiFilter) => {
  const { field, operator, value } = apiFilter;
  if (isRangeOperator(operator) && Array.isArray(value)) {
    return {
      field,
      operator,
      value: String(value[0] ?? ""),
      value2: String(value[1] ?? ""),
    };
  }
  if (isListOperator(operator) && Array.isArray(value)) {
    return { field, operator, value: value.join(", "), value2: "" };
  }
  return { field, operator, value: value == null ? "" : String(value), value2: "" };
};

/**
 * Turns one filter row into the `{ field, operator, value }` shape the backend
 * expects, applying the field's type and the operator's arity.
 * @param {object} filter the filter row ({ field, operator, value, value2 }).
 * @param {Map<string, object>} fieldByKey lookup from field key to metadata.
 * @returns {object} the API filter clause.
 */
const toApiFilter = (filter, fieldByKey) => {
  const type = fieldByKey.get(filter.field)?.type;
  if (isRangeOperator(filter.operator)) {
    return {
      field: filter.field,
      operator: filter.operator,
      value: [coerce(filter.value, type), coerce(filter.value2, type)],
    };
  }
  if (isListOperator(filter.operator)) {
    return {
      field: filter.field,
      operator: filter.operator,
      value: String(filter.value)
        .split(",")
        .map((part) => coerce(part.trim(), type))
        .filter((v) => v !== ""),
    };
  }
  return {
    field: filter.field,
    operator: filter.operator,
    value: coerce(filter.value, type),
  };
};

/** Aggregate functions the backend supports, with display labels. */
export const AGGREGATE_FNS = [
  { value: "count", label: "Count" },
  { value: "sum", label: "Sum" },
  { value: "avg", label: "Average" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maximum" },
];

/**
 * Whether a field can be used with an aggregate function. `count` works on any
 * field because it counts rows; the rest need a numerically aggregatable field.
 * @param {object} field the field metadata.
 * @param {string} fn the aggregate function.
 * @returns {boolean}
 */
export const canAggregate = (field, fn) =>
  !!field && (fn === "count" || !!field.aggregatable);

/**
 * Creates a blank aggregation row.
 * @param {string} field the field key to default to.
 * @returns {{fn: string, field: string}}
 */
export const emptyAggregation = (field = "") => ({ fn: "count", field });

/**
 * Result-column alias the backend returns an aggregate under, e.g. `sum_value`.
 * @param {string} fn the aggregate function.
 * @param {string} field the aggregated field key.
 * @returns {string}
 */
export const aggregateAlias = (fn, field) => `${fn}_${field}`;

/**
 * Human label for a result column, resolving aggregate aliases such as
 * `count_kpi` back to "Count of KPI" using the field metadata.
 * @param {string} columnKey the column key returned by the API.
 * @param {Map<string, object>} fieldByKey lookup from field key to metadata.
 * @returns {string} the display label.
 */
export const columnLabel = (columnKey, fieldByKey) => {
  if (fieldByKey.has(columnKey)) return fieldByKey.get(columnKey).label;
  const fn = AGGREGATE_FNS.find((f) => columnKey.startsWith(`${f.value}_`));
  if (fn) {
    const fieldKey = columnKey.slice(fn.value.length + 1);
    const field = fieldByKey.get(fieldKey);
    if (field) return `${fn.label} of ${field.label}`;
  }
  return columnKey;
};

/**
 * Assembles the API query config from builder state, dropping incomplete rows.
 * Aggregations and groupBy are only sent when present, so an unsummarised query
 * keeps the plain row shape.
 * @param {string} module the selected module key.
 * @param {string[]} fields the selected field keys.
 * @param {object[]} filters the filter rows.
 * @param {object[]} metadataFields the module's field metadata list.
 * @param {object[]} aggregations the aggregation rows.
 * @param {string[]} groupBy the group-by field keys.
 * @returns {object} the query config to preview or save.
 */
export const buildQueryConfig = (
  module,
  fields,
  filters,
  metadataFields,
  aggregations = [],
  groupBy = [],
) => {
  const fieldByKey = new Map(metadataFields.map((f) => [f.key, f]));
  const validAggregations = aggregations.filter(
    (a) => a.field && canAggregate(fieldByKey.get(a.field), a.fn),
  );
  return {
    module,
    fields,
    filters: filters
      .filter((f) => f.field && f.operator && f.value !== "")
      .map((f) => toApiFilter(f, fieldByKey)),
    ...(validAggregations.length
      ? { aggregations: validAggregations, groupBy }
      : {}),
  };
};

/**
 * Field keys the builder never surfaces — as a column, filter, aggregation, or
 * preview column. `locationId` is scope-only (driven by the location picker);
 * `periodStart`/`periodEnd` (Start/End Date) are intentionally not offered here.
 */
export const HIDDEN_FIELD_KEYS = ["locationId", "periodStart", "periodEnd"];

/**
 * Readable label for a location from `getSource`. That endpoint returns
 * `location` as address parts rather than a string, so prefer the unit code and
 * fall back to composing the address — mirrors the Set Due Date screen.
 * @param {object} loc the location record.
 * @returns {string} a displayable label.
 */
export const locationLabel = (loc) => {
  if (!loc) return "";
  if (loc.unitCode) return loc.unitCode;
  const parts = [loc.location?.area, loc.location?.city].filter(Boolean);
  return parts.length ? parts.join(", ") : `Location ${loc.id}`;
};
